import { reactive, toRefs } from 'vue';
import { gql } from '@apollo/client/core';
import { executeQuery } from '@/components/commons/apolloUtils';
import useExperiment from '@/compositions/useExperiment';
import idImageDesktop from '@/assets/interior-design/ID-desktop.jpg';
import idImageMobile from '@/assets/interior-design/ID-mobile.jpg';
import ServerContextUtils, { SERVER_CONTEXT_HEAD } from '@/components/commons/ServerContextUtils';
import Tracking from '@/components/commons/Tracking';

const CURRENT_HOME_PAGE_QUERY = gql`
  query CURRENT_HOME_PAGE {
    getCurrentHomePage {
      success
      pages {
        id
        title
        description
        tiles {
          id
          type
          actionType
          ... on ImageTile {
            url
            desktopImage {
              url
              width
              height
            }
            mobileImage {
              url
              width
              height
            }
            products
          }
          ... on AnimationTile {
            desktopImage {
              url
              width
              height
            }
            mobileImage {
              url
              width
              height
            }
            desktopAnimation
            mobileAnimation
          }
          ... on ProductCarouselTile {
            onSaleFilter
            displayTitle
          }
          ... on NewsletterTile {
            displayTitle
            desktopImage {
              url
            }
            mobileImage {
              url
            }
          }
          ... on SettingShotsCarouselTile {
            displayTitle
          }
          ... on Recommender {
            displayTitle
            altText
          }
        }
        carouselProducts
      }
      reviews {
        comment
        rating
        name
        location
        product
      }
    }
  }
`;

const HOME_PAGE_QUERY = gql`
  query HOME_PAGE($pageId: ID!, $slug: String!) {
    getHomePage(pageId: $pageId, slug: $slug) {
      success
      pages {
        id
        title
        description
        tiles {
          id
          type
          actionType
          ... on ImageTile {
            url
            desktopImage {
              url
              width
              height
            }
            mobileImage {
              url
              width
              height
            }
            products
          }
          ... on AnimationTile {
            desktopImage {
              url
              width
              height
            }
            mobileImage {
              url
              width
              height
            }
            desktopAnimation
            mobileAnimation
          }
          ... on ProductCarouselTile {
            onSaleFilter
            displayTitle
          }
          ... on NewsletterTile {
            displayTitle
            desktopImage {
              url
            }
            mobileImage {
              url
            }
          }
          ... on SettingShotsCarouselTile {
            displayTitle
          }
          ... on Recommender {
            displayTitle
            altText
          }
        }
        carouselProducts
      }
      reviews {
        comment
        rating
        name
        location
        product
      }
    }
  }
`;

const state = reactive({
  id: null,
  title: null,
  description: null,
  pages: {},
  reviews: [],
  tiles: [],
  carouselProducts: {},
  showModal: false,
  disableImageTaggingAndModal: false,
  enableImageTagging: true,
  enableOnlyProductModalList: false,
});

export default function useHome() {
  async function fetchData({ pageId = 0, slug = '' } = {}) {
    try {
      let homePageData;
      if (pageId && slug) {
        const result = await executeQuery(HOME_PAGE_QUERY, {
          pageId,
          slug,
        });
        homePageData = result.getHomePage;
      } else {
        const result = await executeQuery(CURRENT_HOME_PAGE_QUERY);
        homePageData = result.getCurrentHomePage;
      }
      if (homePageData.pages?.length) {
        updateHome(JSON.parse(JSON.stringify(homePageData.pages[0])));
        updateTiles(homePageData.pages[0].tiles);
        updateReviews(homePageData.reviews);
        updateCarouselProducts(homePageData.pages[0].carouselProducts);
      }
    } catch (error) {
      console.error('Home', error);
    }
  }

  function initInteriorDesignExp() {
    const { isFeatureVariant } = useExperiment();
    const enabled = isFeatureVariant('interior-design-tile', 'on');

    if (enabled) {
      const interior = {
        id: 'interior-design',
        type: 'FULL_WIDTH_IMAGE',
        actionType: 'URL',
        altText: 'Url: https://interior-design.article.com',
        desktopImage: {
          key: 'tiles/tile3003/3840w/7244.jpg',
          url: `${idImageDesktop}?fit=max&w={width}&q={q}`,
          alt: 'Url: https://interior-design.article.com',
          width: 3840,
          height: 960,
        },
        mobileImage: {
          key: 'tiles/tile3003/1536w/7245.jpg',
          url: `${idImageMobile}?fit=max&w={width}&q={q}`,
          alt: 'Url: https://interior-design.article.com',
          width: 1536,
          height: 1944,
        },
        url: 'https://interior-design.article.com',
      };

      if (state.tiles.length > 6) {
        const dupTiles = [...state.tiles];
        dupTiles.splice(6, 0, interior);
        updateTiles(dupTiles);
      } else {
        updateTiles([...state.tiles, interior]);
      }

      Tracking.track('interior-design-tile-home-page', {
        on: true,
      });
    }
  }

  function updateHome(payload) {
    if (payload && Object.keys(payload).length) {
      for (const key of Object.keys(payload)) {
        state[key] = payload[key];
      }
    }
  }

  function updateShowProductListModal(payload) {
    state.showModal = payload;
  }

  function updateTiles(payload) {
    state.tiles = payload;

    if (import.meta.env.SSR) {
      if (state.tiles[0].type === 'FULL_WIDTH_IMAGE') {
        setPreloadImage(state.tiles[0].mobileImage.url);
      }
    }
  }

  function updateReviews(payload) {
    state.reviews = payload;
  }

  function updateCarouselProducts(payload) {
    state.carouselProducts = payload;
  }

  function setPreloadImage(url) {
    const widths = [320, 425, 768, 970];
    const preloadString = `<link rel="preload" as="image" imagesrcset="{href}" media="{media}" />`;
    const sets = widths.map((width) => {
      const arr = [
        url.replace('{width}', width).replace('{q}', 80) + `&fm=webp 1x`,
        url.replace('{width}', width * 2).replace('{q}', 40) + `&fm=webp 2x`,
      ];

      return preloadString.replace('{href}', arr.join(', ')).replace('{media}', `(min-width: ${width}px)`);
    });
    ServerContextUtils.putItem(SERVER_CONTEXT_HEAD, sets.join(''));
  }

  return {
    ...toRefs(state),
    fetchData,
    initInteriorDesignExp,
    updateHome,
    updateShowProductListModal,
    updateTiles,
    updateReviews,
    updateCarouselProducts,
  };
}
